import React, { FC, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';

import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import Typography from '@mui/material/Typography';

import { useUnstake } from '@services/contract/bonds/rewardPool.contract';

import { useAuth } from '@contexts/common/AuthorizationContext';
import { modalActions } from '@contexts/common/ModalContext';

import useBondsInfo from '@hooks/bonds/useBondsInfo';
import useNftPoolInfo from '@hooks/bonds/useNftPoolInfo';

import { modifyEvent } from '@helpers/common/form.helpers';

import { formatBNWithCommas, toBN } from '@utils/formaters.utils';

import { amountYup } from '@validators/vaults/common.validators';

import Button from '@components/common/Button';
import Input from '@components/common/Input';
import ModalWrapper from '@components/common/Modal/ModalWrapper';
import SkeletonLoader from '@components/common/SkeletonLoader';
import SwitchChainButton from '@components/common/SwitchChainButton';

import { BONDS_UNSTAKE_DESCRIPTION } from '@constants/bonds/common.constants';
import {
  DEFAULT_CRITERIA_MODE,
  DEFAULT_REVALIDATE_MODE,
  FieldNames,
} from '@constants/common/form.constants';
import { ONLY_POSITIVE_INTEGERS } from '@constants/common/regex.constants';
import { Size } from '@constants/common/ui.constants';

import useStyles from './styles.module';

type FormValues = {
  [FieldNames.Amount]: string;
};

type BondsInfo = NonNullable<ReturnType<typeof useBondsInfo>['data']>;

const UnstakeBondsModalContent: FC<{ bondsInfo: BondsInfo }> = ({
  bondsInfo,
}) => {
  const { chainId, bondPriceUsd } = bondsInfo;
  const { classes } = useStyles();
  const { account } = useAuth();

  const { data: poolData, isPending: isPendingPool } = useNftPoolInfo({
    chainId,
    nftPriceUsd: bondPriceUsd,
  });

  const stakeAmount = poolData?.stakeAmount ?? toBN(0);

  const { unstake, isPending: isPendingUnstake } = useUnstake(chainId, () =>
    Promise.resolve(modalActions.hide()),
  );

  const validationSchema = yup.object().shape({
    amount: amountYup(stakeAmount),
  });

  const {
    control,
    handleSubmit,
    trigger,
    setValue,
    formState: { isValid },
  } = useForm<FormValues>({
    mode: DEFAULT_CRITERIA_MODE,
    reValidateMode: DEFAULT_REVALIDATE_MODE,
    resolver: yupResolver(validationSchema),
    defaultValues: { [FieldNames.Amount]: '' },
  });

  const onSubmit = async (formData: FormValues) => {
    await trigger();
    // Bernard's useUnstake fires writeContract synchronously (no awaitable);
    // the modal closes via the handleUpdate passed to useUnstake on onSuccess.
    unstake(BigInt(formData[FieldNames.Amount]));
  };

  const onMaxHandler = useCallback(() => {
    if (!account || stakeAmount.isZero()) return;
    setValue(FieldNames.Amount, stakeAmount.toFixed(0), {
      shouldValidate: true,
    });
  }, [stakeAmount, account, setValue]);

  const isDisableUnstake = stakeAmount.isZero() || !isValid;

  return (
    <ModalWrapper title="Unstake Bonds">
      <form className={classes.container} onSubmit={handleSubmit(onSubmit)}>
        <div className={classes.noticeCard}>
          <Typography
            variant="body2"
            color={(theme) => theme.palette.custom.text.light}
            align="center"
          >
            {BONDS_UNSTAKE_DESCRIPTION}
          </Typography>
        </div>

        <div className={classes.balanceInfo}>
          {isPendingPool || !poolData ? (
            <SkeletonLoader
              className={classes.skeleton}
              classNameWrap={classes.skeleton}
            />
          ) : (
            <Typography
              variant="caption1"
              color={(theme) => theme.palette.custom.text.accent}
            >
              {`Stake Balance: ${formatBNWithCommas(stakeAmount, 0)}`}
            </Typography>
          )}
          <div className={classes.inputAndButton}>
            <Controller
              control={control}
              name={FieldNames.Amount}
              render={({
                field: { value, onBlur, onChange },
                fieldState: { error },
              }) => (
                <Input
                  value={value}
                  onBlur={onBlur}
                  onChange={(e) =>
                    onChange(modifyEvent(e, ONLY_POSITIVE_INTEGERS))
                  }
                  placeholder="0"
                  onMax={onMaxHandler}
                  inputError={error?.message}
                  disabled={!account}
                />
              )}
            />
            <SwitchChainButton chainId={chainId} size={Size.Large}>
              <Button
                buttonType="submit"
                disabled={isDisableUnstake}
                isLoading={isPendingUnstake}
              >
                Unstake
              </Button>
            </SwitchChainButton>
          </div>
        </div>
      </form>
    </ModalWrapper>
  );
};

const UnstakeBondsModal: FC = () => {
  const { data: bondsInfo } = useBondsInfo();

  if (!bondsInfo) return null;

  return <UnstakeBondsModalContent bondsInfo={bondsInfo} />;
};

export default UnstakeBondsModal;
